import { Link, useParams } from 'react-router';

import { useComponents, useProposal } from '@/api/queries';
import { NotFoundPage } from '@/app/errors';
import { useNow } from '@/app/preferences';
import { proposalPath, runsPath, scopePath } from '@/app/scope';
import { HealthBadge, ProposalStatusBadge, Tag, type TagTone } from '@/components/badges';
import { Breadcrumb } from '@/components/Breadcrumb';
import { Button } from '@/components/Button';
import { ArrowRight, CheckCircle, CrossCircle, ExternalLink, Warning } from '@/components/icons';
import { DIALOG_LINGER_MS, useLingering } from '@/components/motion';
import {
  EmptyState,
  ErrorNotice,
  Kv,
  Loading,
  Notice,
  Panel,
  Table,
  Td,
  Th,
} from '@/components/primitives';
import { Timestamp } from '@/components/Timestamp';
import { SCOPE_LABEL } from '@/domain/health';
import { formatDuration } from '@/domain/time';
import type { ProposalDetail, PublishAttempt } from '@/domain/types';

import { useState } from 'react';

import { ConflictDialog } from './dialogs';
import { EvidenceList } from './EvidenceList';
import { conflictOf, type Conflict } from './conflicts';
import { ProposalActions } from './ProposalActions';
import { kindLabel, problemSince, reasonLabel } from '../queue/queue';

/** Proposal detail (TDD §5.3): the full record, its evidence, and every publish attempt. */
export function ProposalPage() {
  const { proposalId } = useParams();
  if (!proposalId) return <NotFoundPage />;
  return <Detail proposalId={decodeURIComponent(proposalId)} />;
}

function Detail({ proposalId }: { proposalId: string }) {
  const query = useProposal(proposalId);
  if (query.isPending) return <Loading />;
  if (query.isError) {
    return query.error.message.startsWith('No proposal') ? (
      <NotFoundPage />
    ) : (
      <ErrorNotice
        message={`Could not load the proposal: ${query.error.message}`}
        action={
          <Button size="sm" onClick={() => query.refetch()}>
            Try again
          </Button>
        }
      />
    );
  }
  return <DetailBody detail={query.data} />;
}

function DetailBody({ detail }: { detail: ProposalDetail }) {
  const { proposal, attempts, evidence_runs, display_name } = detail;
  const components = useComponents(proposal.scope);
  const view = components.data?.components.find(
    (c) => c.config.capability_id === proposal.capability_id,
  );
  const now = useNow();
  const since = problemSince(proposal);
  const isSystem = proposal.created_by.startsWith('system:');
  const [conflict, setConflict] = useState<Conflict | null>(null);
  const shownConflict = useLingering(conflict, DIALOG_LINGER_MS);

  const decision: { label: string; value: React.ReactNode }[] = [
    { label: 'Proposal', value: <code className="break-all">{proposal.proposal_id}</code> },
    {
      label: 'Created',
      value: (
        <>
          <Timestamp iso={proposal.created_at} mode="full" /> by{' '}
          {isSystem ? <code>{proposal.created_by}</code> : proposal.created_by}
        </>
      ),
    },
    { label: 'Reason', value: reasonLabel(proposal.reason) },
    { label: 'Version', value: <span className="tabular">{proposal.version}</span> },
  ];
  if (proposal.note) decision.push({ label: 'Note', value: proposal.note });
  if (proposal.approved_by) {
    decision.push({
      label: proposal.auto_approved ? 'Approved automatically' : 'Approved',
      value: (
        <>
          {proposal.approved_at ? <Timestamp iso={proposal.approved_at} mode="full" /> : null} by{' '}
          {proposal.approved_by.startsWith('system:') ? (
            <code>{proposal.approved_by}</code>
          ) : (
            proposal.approved_by
          )}
        </>
      ),
    });
  }
  if (proposal.rejected_by) {
    decision.push({
      label: 'Rejected',
      value: (
        <>
          {proposal.rejected_at ? <Timestamp iso={proposal.rejected_at} mode="full" /> : null} by{' '}
          {proposal.rejected_by}
        </>
      ),
    });
    if (proposal.rejection_reason)
      decision.push({ label: 'Rejection reason', value: proposal.rejection_reason });
  }
  if (proposal.incident_action) {
    decision.push({
      label: 'Incident decision',
      value: {
        none: 'Status change only',
        open: 'Open a new incident',
        attach: 'Add to an open incident',
        auto: 'Decided by the system',
      }[proposal.incident_action],
    });
  }
  if (proposal.incident_title)
    decision.push({ label: 'Incident title', value: proposal.incident_title });
  if (proposal.incident_body)
    decision.push({
      label: 'Incident update',
      value: <span className="whitespace-pre-wrap">{proposal.incident_body}</span>,
    });
  if (proposal.resolution_body)
    decision.push({
      label: 'Resolution text',
      value: <span className="whitespace-pre-wrap">{proposal.resolution_body}</span>,
    });
  if (proposal.withdrawn_reason)
    decision.push({ label: 'Withdrawn', value: proposal.withdrawn_reason.replaceAll('_', ' ') });
  // Links inside a value are underlined: colour alone must not carry them.
  if (proposal.supersedes)
    decision.push({
      label: 'Supersedes',
      value: (
        <Link to={proposalPath(proposal.supersedes)} className="underline">
          an earlier proposal
        </Link>
      ),
    });
  if (proposal.superseded_by)
    decision.push({
      label: 'Superseded by',
      value: (
        <Link to={proposalPath(proposal.superseded_by)} className="underline">
          a newer proposal
        </Link>
      ),
    });
  if (proposal.published_at)
    decision.push({
      label: 'Published',
      value: <Timestamp iso={proposal.published_at} mode="full" />,
    });
  if (proposal.published_link) {
    decision.push({
      label: 'Incident link',
      value: (
        <a
          href={proposal.published_link}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 break-all"
        >
          {proposal.published_link} <ExternalLink size={12} />
        </a>
      ),
    });
  }
  if (proposal.last_error)
    decision.push({
      label: 'Last error',
      value: <span className="text-danger-ink">{proposal.last_error}</span>,
    });

  return (
    <div className="flex flex-col gap-5">
      <Breadcrumb
        items={[
          { label: SCOPE_LABEL[proposal.scope], to: scopePath(proposal.scope) },
          { label: 'Approvals', to: scopePath(proposal.scope, 'queue') },
          { label: 'Proposal' },
        ]}
      />

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-page font-medium flex flex-wrap items-center gap-x-3 gap-y-1">
            {display_name}
            <span className="text-body font-normal text-ink-3">{kindLabel(proposal)}</span>
            <ProposalStatusBadge status={proposal.status} />
          </h1>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <HealthBadge health={proposal.from_status} />
            <ArrowRight size={14} className="text-ink-3" />
            <HealthBadge health={proposal.to_status} />
            <span className="text-body text-ink-2 ml-2">
              {proposal.evidence.length > 0 ? (
                <>
                  Problem for {formatDuration(now - Date.parse(since))}, since{' '}
                  <Timestamp iso={since} mode="auto" />
                </>
              ) : (
                'Made by hand'
              )}
            </span>
          </div>
        </div>
        <ProposalActions
          proposal={proposal}
          displayName={display_name}
          onOutcome={(outcome) => {
            const c = conflictOf(outcome, proposal, display_name);
            if (c) setConflict(c);
          }}
        />
      </div>
      {shownConflict ? (
        <ConflictDialog
          before={shownConflict.before}
          current={shownConflict.current}
          displayName={shownConflict.displayName}
          open={conflict !== null}
          onClose={() => setConflict(null)}
        />
      ) : null}

      {proposal.status === 'approved' ? (
        <Notice tone="info">
          Publishing. If nothing happens within 2 minutes it is marked failed and returns to the
          queue.
        </Notice>
      ) : null}
      {view?.state.blind_since ? (
        <Notice tone="warning">
          Blind: no run since{' '}
          {view.state.last_observed_at ? (
            <Timestamp iso={view.state.last_observed_at} mode="auto" />
          ) : (
            'it was configured'
          )}
          .
        </Notice>
      ) : null}

      {/* items-start: the decision card is as tall as its facts, not as the evidence beside it. */}
      <div className="grid items-start gap-5 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]">
        <Panel title="Decision">
          <Kv items={decision} />
          <p className="mt-4 text-body">
            <Link to={runsPath(proposal.scope, proposal.capability_id)}>Run history</Link>
          </p>
        </Panel>
        <Panel
          title="Evidence"
          description={
            proposal.evidence.length > 0
              ? `${proposal.evidence.length} ${proposal.evidence.length === 1 ? 'run' : 'runs'}, newest first`
              : undefined
          }
        >
          <EvidenceList
            evidence={proposal.evidence}
            runs={evidence_runs}
            hadGap={proposal.had_gap}
            checksOrder={view?.config.checks}
          />
        </Panel>
      </div>

      <Panel title="Publish attempts" bodyClassName="p-0">
        {attempts.length === 0 ? (
          <EmptyState title="No publish attempts yet" compact />
        ) : (
          <AttemptsTable attempts={attempts} />
        )}
      </Panel>
    </div>
  );
}

/** The publisher's outcome words (TDD §6.7), as operators read them, with the tone and icon each takes. */
const OUTCOME: Record<
  PublishAttempt['outcome'],
  { label: string; tone: TagTone; icon: React.ReactNode }
> = {
  success: { label: 'Success', tone: 'success', icon: <CheckCircle size={12} /> },
  retryable: { label: 'Retryable failure', tone: 'warning', icon: <Warning size={12} /> },
  uncertain: { label: 'No response', tone: 'warning', icon: <Warning size={12} /> },
  conflict: { label: 'Conflict', tone: 'danger', icon: <CrossCircle size={12} /> },
};

function AttemptsTable({ attempts }: { attempts: PublishAttempt[] }) {
  const dash = <span className="text-ink-3">—</span>;
  return (
    <Table className="[&>tbody>tr:last-child>td]:border-b-0">
      <thead>
        <tr>
          <Th className="pl-5 text-right">#</Th>
          <Th>Time</Th>
          <Th>Requested by</Th>
          <Th>Command</Th>
          <Th>Outcome</Th>
          <Th className="text-right">HTTP</Th>
          <Th>Error</Th>
          <Th className="pr-5">Incident</Th>
        </tr>
      </thead>
      <tbody>
        {attempts.map((a) => (
          <tr key={a.attempt_seq}>
            <Td className="pl-5 text-right tabular">{Number(a.attempt_seq)}</Td>
            <Td>
              <Timestamp iso={a.attempted_at} mode="auto" withSeconds />
            </Td>
            <Td>
              {a.requested_by.startsWith('system:') ? (
                <code>{a.requested_by}</code>
              ) : (
                a.requested_by
              )}
            </Td>
            <Td>{a.action_taken ? <code>{a.action_taken}</code> : dash}</Td>
            <Td>
              <Tag tone={OUTCOME[a.outcome].tone} icon={OUTCOME[a.outcome].icon}>
                {OUTCOME[a.outcome].label}
              </Tag>
            </Td>
            <Td className="text-right tabular">{a.http_status ?? 'no response'}</Td>
            <Td className="max-w-[320px] break-words">{a.error ?? dash}</Td>
            <Td className="pr-5">
              {a.incident_link ? (
                <a
                  href={a.incident_link}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1"
                >
                  Open <ExternalLink size={12} />
                </a>
              ) : (
                dash
              )}
            </Td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}
